import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import { ensureRoleFields } from '../../../utils/wizardHelpers';

export const useStaffingFormData = (
  initialData, 
  outcomeId, 
  outcomeName, 
  outcomeData, 
  onUpdate, 
  autoSave = true, 
  readOnly = false
) => {
  const initializedRef = useRef(false);
  const lastSavedDataRef = useRef(null);
  
  // Helper function to create empty milestone structure
  const createEmptyMilestones = useCallback((milestones) => {
    if (!milestones || milestones.length === 0) return [];
    return milestones.map(milestone => ({
      ...milestone,
      capex_classification: milestone.capex_classification || 'OPEX',
      roles: [] // Start with empty roles - users will add manually
    }));
  }, []);

  // Helper function to sync milestones with outcome data
  const syncMilestonesWithOutcome = useCallback((currentStaffingData, outcomeMilestones) => {
    if (!outcomeMilestones || outcomeMilestones.length === 0) {
      return currentStaffingData;
    }

    const existingMilestones = currentStaffingData.milestones || [];
    const existingMilestoneIds = new Set(existingMilestones.map(m => m.id));

    // Find milestones that exist in outcome but not in staffing
    const missingMilestones = outcomeMilestones.filter(m => !existingMilestoneIds.has(m.id));

    // Find milestones that exist in staffing but not in outcome (should be removed)
    const validMilestones = existingMilestones.filter(m => 
      outcomeMilestones.some(om => om.id === m.id)
    );

    // Add missing milestones with empty roles array
    const newMilestones = missingMilestones.map(milestone => ({
      ...milestone,
      capex_classification: milestone.capex_classification || 'OPEX',
      roles: []
    }));

    // Update existing milestones with latest outcome data (preserve roles)
    const updatedMilestones = validMilestones.map(staffingMilestone => {
      const outcomeMilestone = outcomeMilestones.find(om => om.id === staffingMilestone.id);
      return {
        ...staffingMilestone,
        // Update core milestone properties from outcome
        name: outcomeMilestone.name,
        start_date: outcomeMilestone.start_date,
        end_date: outcomeMilestone.end_date,
        capex_classification: outcomeMilestone.capex_classification || staffingMilestone.capex_classification || 'OPEX',
        // Preserve existing roles and staffing-specific data
        roles: staffingMilestone.roles || []
      };
    });

    console.log('StaffingFormData: Syncing milestones with outcome data');
    console.log('Outcome milestones:', outcomeMilestones.map(m => ({ id: m.id, name: m.name })));
    console.log('Missing milestones:', missingMilestones.map(m => ({ id: m.id, name: m.name })));
    console.log('Updated milestones:', updatedMilestones.map(m => ({ id: m.id, name: m.name, rolesCount: m.roles?.length || 0 })));

    return {
      ...currentStaffingData,
      milestones: [...updatedMilestones, ...newMilestones]
    };
  }, []);

  // Form state
  const [formData, setFormData] = useState(() => {
    const initialStartMonth = outcomeData?.start_date ? 
      dayjs(outcomeData.start_date).format('YYYY-MM') : 
      initialData?.start_month || dayjs().format('YYYY-MM');
    
    const initialAdjustedMonths = outcomeData?.start_date && outcomeData?.end_date ?
      Math.max(1, dayjs(outcomeData.end_date).diff(dayjs(outcomeData.start_date), 'month') + 1) :
      initialData?.adjustedMonths || 6;
    
    const milestones = outcomeData?.milestones || [];
    let initialMilestones = [];

    if (initialData?.milestones) {
      // Use existing milestone data, ensure capex_classification exists
      initialMilestones = initialData.milestones.map(milestone => ({
        ...milestone,
        capex_classification: milestone.capex_classification || 'OPEX',
        roles: (milestone.roles || []).map(role => ({
          ...role,
          capex_classification: role.capex_classification || milestone.capex_classification || 'OPEX',
          capex_source: role.capex_source || 'milestone'
        }))
      }));
    } else if (milestones.length > 0) {
      // Create empty milestone structure for manual role creation
      initialMilestones = createEmptyMilestones(milestones);
    } else {
      // No milestones available
      initialMilestones = [];
    }

    return {
      outcome_id: outcomeId,
      outcome_name: outcomeName || '',
      start_month: initialStartMonth,
      adjustedMonths: initialAdjustedMonths,
      milestones: initialMilestones,
      fixedCosts: initialData?.fixedCosts || [],
      // Legacy support
      roles: ensureRoleFields(initialData?.roles || [], initialAdjustedMonths),
      ...initialData
    };
  });

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [autoSaveDisabled, setAutoSaveDisabled] = useState(false);

  // Check if data has changed
  const hasDataChanged = useCallback((newData) => {
    const serializeData = (data) => {
      return JSON.stringify(data, (key, value) => {
        if (typeof value === 'function') return undefined;
        if (key === 'last_updated' || key === 'created_date') return undefined;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const sorted = {};
          Object.keys(value).sort().forEach(k => {
            if (k !== 'last_updated' && k !== 'created_date') {
              sorted[k] = value[k];
            }
          });
          return sorted;
        }
        return value;
      });
    };

    const newDataString = serializeData(newData);
    return lastSavedDataRef.current !== newDataString;
  }, []);

  // Helper function to migrate role from project-relative to calendar format (lazy migration)
  // BACKWARDS COMPATIBILITY: Safe migration that preserves all existing data
  const migrateRoleToCalendarFormat = useCallback((role, milestone) => {
    // BACKWARDS COMPATIBILITY: Always return a valid role object
    if (!role || typeof role !== 'object') {
      console.warn('🔍 Backwards Compatibility: Invalid role object detected, creating safe default');
      return {
        id: `safe-role-${Date.now()}`,
        name: 'Unknown Role',
        allocation: 100,
        rate: 0,
        dateRanges: [],
        timelineVersion: '3.0'
      };
    }

    // BACKWARDS COMPATIBILITY: Handle roles without dateRanges (legacy format)
    if (!role.dateRanges || role.dateRanges.length === 0) {
      console.log(`🔍 Backwards Compatibility: Role ${role.name || role.id} has no dateRanges, preserving as-is`);
      return {
        ...role, // Preserve ALL existing fields
        // Only add missing essential fields if they don't exist
        timelineVersion: role.timelineVersion || '2.0', // Don't force upgrade
        allocation: role.allocation || 100,
        rate: role.rate || 0
      };
    }

    // BACKWARDS COMPATIBILITY: Check if already in calendar format (don't re-migrate)
    const isAlreadyCalendarFormat = role.dateRanges.every(range => 
      range.timelineVersion === '3.0' || 
      (range.startMonth >= 1 && range.startMonth <= 12 && 
       range.endMonth >= 1 && range.endMonth <= 12 &&
       range.startMonth <= range.endMonth)
    );

    if (isAlreadyCalendarFormat) {
      console.log(`🔍 Backwards Compatibility: Role ${role.name || role.id} already in modern format`);
      return role; // Already migrated - don't touch it
    }

    // BACKWARDS COMPATIBILITY: Safe migration with fallbacks
    try {
      const projectStartMonth = formData.start_month || dayjs().format('YYYY-MM');
      const projectStart = dayjs(projectStartMonth + '-01');
      
      const migratedDateRanges = role.dateRanges.map((range, index) => {
        // BACKWARDS COMPATIBILITY: Handle malformed ranges
        const safeStartMonth = Math.max(1, Number(range.startMonth) || 1);
        const safeEndMonth = Math.max(safeStartMonth, Number(range.endMonth) || safeStartMonth);
        
        const startDate = projectStart.add(safeStartMonth - 1, 'month');
        const endDate = projectStart.add(safeEndMonth - 1, 'month');
        
        // BACKWARDS COMPATIBILITY: Preserve ALL original range fields
        return {
          ...range, // Keep all existing fields
          startMonth: Math.max(1, Math.min(12, startDate.month() + 1)), // Safe calendar month
          endMonth: Math.max(1, Math.min(12, endDate.month() + 1)), // Safe calendar month
          timelineVersion: '3.0', // Mark as migrated
          lastTimelineUpdate: new Date().toISOString(),
          // BACKWARDS COMPATIBILITY: Track migration for debugging
          migratedFrom: {
            originalStartMonth: range.startMonth,
            originalEndMonth: range.endMonth,
            projectStartMonth: projectStartMonth,
            migratedAt: new Date().toISOString()
          }
        };
      });

      console.log(`✅ Backwards Compatibility: Safely migrated role ${role.name || role.id} from project-relative to calendar format`);

      // BACKWARDS COMPATIBILITY: Preserve ALL original role fields
      return {
        ...role, // Keep everything from original
        dateRanges: migratedDateRanges,
        timelineVersion: '3.0',
        lastTimelineUpdate: new Date().toISOString(),
        // BACKWARDS COMPATIBILITY: Track migration history
        migrationHistory: [
          ...(role.migrationHistory || []),
          {
            from: 'project-relative',
            to: 'calendar',
            migratedAt: new Date().toISOString(),
            originalTimelineVersion: role.timelineVersion || 'legacy'
          }
        ]
      };
    } catch (error) {
      console.error('🚨 Backwards Compatibility: Migration failed, preserving original role:', error);
      // BACKWARDS COMPATIBILITY: If migration fails, return original role unchanged
      return {
        ...role,
        migrationError: {
          error: error.message,
          timestamp: new Date().toISOString(),
          preservedOriginal: true
        }
      };
    }
  }, [formData.start_month]);

  // Debounced update function
  const debouncedUpdate = useMemo(
    () => debounce(async (data) => {
      if (onUpdate && !readOnly && autoSave && !saving && !autoSaveDisabled) {
        try {
          setSaving(true);
          // Cancel any pending debounced calls to prevent overlapping saves
          debouncedUpdate.cancel();
          
          const cleanedData = {
            ...data,
            milestones: (data.milestones || []).map(milestone => ({
              ...milestone,
              roles: (milestone.roles || []).map(role => {
                // Trigger lazy migration during auto-save
                const migratedRole = migrateRoleToCalendarFormat(role, milestone);

                return {
                  ...migratedRole,
                  // Ensure numeric fields are properly typed
                  allocation: Number(migratedRole.allocation) || 0,
                  rate: Number(migratedRole.rate) || 0,
                  overlap_months: Number(migratedRole.overlap_months) || 1,
                  // Ensure dateRanges are properly structured with migration applied
                  dateRanges: (migratedRole.dateRanges || []).map(range => ({
                    startMonth: Number(range.startMonth) || 1,
                    endMonth: Number(range.endMonth) || 1,
                    allocation: Number(range.allocation) || 0,
                    timelineVersion: range.timelineVersion || '3.0' // Ensure version is set
                  }))
                };
              })
            })),
            fixedCosts: (data.fixedCosts || []).map(cost => ({
              ...cost,
              monthlyCost: Number(cost.monthlyCost) || 0,
              startMonth: Math.max(1, Number(cost.startMonth) || 1),
              endMonth: Math.max(1, Number(cost.endMonth) || 1)
            })),
            adjustedMonths: Number(data.adjustedMonths) || 6
          };

          await onUpdate(cleanedData);
          
          const serializeData = (data) => {
            return JSON.stringify(data, (key, value) => {
              if (typeof value === 'function') return undefined;
              if (key === 'last_updated' || key === 'created_date') return undefined;
              if (value && typeof value === 'object' && !Array.isArray(value)) {
                const sorted = {};
                Object.keys(value).sort().forEach(k => {
                  if (k !== 'last_updated' && k !== 'created_date') {
                    sorted[k] = value[k];
                  }
                });
                return sorted;
              }
              return value;
            });
          };
          
          lastSavedDataRef.current = serializeData(cleanedData);
          setSaveError(false);
          setLastSaveTime(Date.now());
        } catch (error) {
          console.error('StaffingFormData: Auto-save failed:', error);
          setSaveError(true);
        } finally {
          setSaving(false);
        }
      }
    }, 2000),
    [onUpdate, readOnly, autoSave, saving, autoSaveDisabled, migrateRoleToCalendarFormat]
  );

  // Update form data
  const updateFormData = useCallback((updates) => {
    setFormData(prev => {
      const newData = typeof updates === 'function' ? updates(prev) : { ...prev, ...updates };
      
      // Auto-save if enabled and data has changed
      if (autoSave && !readOnly && !autoSaveDisabled && hasDataChanged(newData)) {
        const timeSinceLastSave = Date.now() - lastSaveTime;
        if (timeSinceLastSave > 3000 && !saving && !isUpdating) {
          setTimeout(() => {
            const currentTimeSinceLastSave = Date.now() - lastSaveTime;
            if (hasDataChanged(newData) && !saving && !isUpdating && !autoSaveDisabled && currentTimeSinceLastSave > 3000) {
              debouncedUpdate(newData);
            }
          }, 500);
        }
      }
      
      return newData;
    });
  }, [autoSave, readOnly, autoSaveDisabled, hasDataChanged, lastSaveTime, saving, isUpdating, debouncedUpdate]);

  // Manual save
  const handleSave = useCallback(async () => {
    if (readOnly || !onUpdate) return;

    try {
      setSaving(true);
      
      const validatedData = {
        ...formData,
        milestones: (formData.milestones || []).map(milestone => ({
          ...milestone,
          roles: (milestone.roles || []).map(role => {
            // Trigger lazy migration during manual save
            const migratedRole = migrateRoleToCalendarFormat(role, milestone);

            return {
              ...migratedRole,
              allocation: Math.max(0, Math.min(100, Number(migratedRole.allocation) || 0)),
              rate: Math.max(0, Number(migratedRole.rate) || 0),
              overlap_months: Math.max(1, Number(migratedRole.overlap_months) || 1),
              dateRanges: (migratedRole.dateRanges || []).map(range => ({
                startMonth: Math.max(1, Number(range.startMonth) || 1),
                endMonth: Math.max(1, Number(range.endMonth) || 1),
                allocation: Math.max(0, Math.min(100, Number(range.allocation) || 0)),
                timelineVersion: range.timelineVersion || '3.0' // Ensure version is set
              })),
              name: migratedRole.name || 'Unknown',
              company: migratedRole.company || 'CA',
              location: migratedRole.location || 'US',
              function: migratedRole.function || 'Other',
              capex_classification: migratedRole.capex_classification || 'OPEX'
            };
          })
        })),
        fixedCosts: (formData.fixedCosts || []).map(cost => ({
          ...cost,
          monthlyCost: Math.max(0, Number(cost.monthlyCost) || 0),
          start_date: cost.start_date || dayjs().format('YYYY-MM'),
          end_date: cost.end_date || dayjs().format('YYYY-MM'),
          type: cost.type || 'Licenses',
          company: cost.company || '' // Empty string if not specified
        })),
        adjustedMonths: Math.max(1, Number(formData.adjustedMonths) || 6)
      };

      await onUpdate(validatedData);
      
      const serializeData = (data) => {
        return JSON.stringify(data, (key, value) => {
          if (typeof value === 'function') return undefined;
          if (key === 'last_updated' || key === 'created_date') return undefined;
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            const sorted = {};
            Object.keys(value).sort().forEach(k => {
              if (k !== 'last_updated' && k !== 'created_date') {
                sorted[k] = value[k];
              }
            });
            return sorted;
          }
          return value;
        });
      };
      
      lastSavedDataRef.current = serializeData(validatedData);
      setSaveError(false);
      setLastSaveTime(Date.now());
    } catch (error) {
      console.error('StaffingFormData: Manual save failed:', error);
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }, [formData, onUpdate, readOnly, migrateRoleToCalendarFormat]);

  // Initialize form data and set up auto-save
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      const serializeData = (data) => {
        return JSON.stringify(data, (key, value) => {
          if (typeof value === 'function') return undefined;
          if (key === 'last_updated' || key === 'created_date') return undefined;
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            const sorted = {};
            Object.keys(value).sort().forEach(k => {
              if (k !== 'last_updated' && k !== 'created_date') {
                sorted[k] = value[k];
              }
            });
            return sorted;
          }
          return value;
        });
      };
      lastSavedDataRef.current = serializeData(formData);
      return;
    }
  }, [formData]);

  // Sync with outcome milestones
  useEffect(() => {
    if (outcomeData?.milestones) {
      setFormData(prev => {
        const syncedData = syncMilestonesWithOutcome(prev, outcomeData.milestones);
        const milestonesChanged = JSON.stringify(syncedData.milestones) !== JSON.stringify(prev.milestones);
        if (milestonesChanged) {
          return syncedData;
        }
        return prev;
      });
    }
  }, [outcomeData?.milestones, syncMilestonesWithOutcome]);

  // Listen for milestone changes from other components
  useEffect(() => {
    const handleMilestoneChange = (event) => {
      if (event.detail.outcomeId === outcomeId) {
        console.log('StaffingFormData: Outcome milestones changed, syncing...');
        const updatedMilestones = event.detail.milestones;
        setFormData(prev => {
          const syncedData = syncMilestonesWithOutcome(prev, updatedMilestones);
          return syncedData;
        });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('outcomeMilestonesChanged', handleMilestoneChange);
      return () => {
        window.removeEventListener('outcomeMilestonesChanged', handleMilestoneChange);
      };
    }
  }, [outcomeId, syncMilestonesWithOutcome]);

  // Merge refreshed backend role objects into formData by role ID without triggering auto-save.
  // The backend already persisted the new rates; this just syncs the UI state.
  const applyUpdatedRoles = useCallback((updatedFlatRoles) => {
    if (!updatedFlatRoles?.length) return;
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(milestone => ({
        ...milestone,
        roles: milestone.roles.map(role => {
          const updated = updatedFlatRoles.find(r => r.id === role.id);
          return updated ? { ...role, ...updated } : role;
        })
      }))
    }));
  }, []);

  return {
    // Data
    formData,

    // State
    saving,
    saveError,
    isUpdating,
    lastSaveTime,
    autoSaveDisabled,

    // Actions
    updateFormData,
    setIsUpdating,
    setAutoSaveDisabled,
    handleSave,
    hasDataChanged,
    migrateRoleToCalendarFormat,
    applyUpdatedRoles,

    // Helpers
    createEmptyMilestones,
    syncMilestonesWithOutcome
  };
};